import React, { useEffect, useState } from 'react';
import { Loader2, Zap, ZapOff, AlertTriangle } from 'lucide-react';
import { llmStatusApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

/**
 * Compact admin pill that surfaces the LLM kill-switch state and lets a
 * super-admin toggle it inline.
 *
 *  Active  →  "🛑 LLM disabled"  (rose pill, click to enable)
 *  Off     →  "⚡ LLM live"       (emerald pill, click to disable)
 *
 *  When the env var `LLM_KILLSWITCH=true` is set on the backend, the pill is
 *  shown as locked (with a small tooltip) and the toggle is disabled — admins
 *  have to remove the env var + restart to flip the switch from that mode.
 */
export default function LlmStatusPill({ className = '' }) {
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Only admins see this pill at all.
  const role = user?.role;
  const isAdmin = role === 'super_admin' || role === 'org_admin';
  const isSuper = role === 'super_admin';

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await llmStatusApi.get();
        if (!cancelled) setState(r);
      } catch (_e) { /* silently ignore — pill just won't render */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    // Light refresh every 30s so the pill stays in sync if someone toggled
    // it from another browser/tab.
    const id = setInterval(async () => {
      try { setState(await llmStatusApi.get()); } catch { /* ignore */ }
    }, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [isAdmin]);

  if (!isAdmin || loading || !state) return null;

  const active = !!state.active;
  const envLocked = !!state.env_locked;

  const toggle = async () => {
    if (!isSuper) return;
    if (envLocked && active) {
      toast.error('LLM_KILLSWITCH env var is set on the server. Remove it and restart to enable LLM.');
      return;
    }
    setBusy(true);
    try {
      const next = await llmStatusApi.setActive(!active);
      setState((prev) => ({ ...(prev || {}), ...next, db_flag: !active && !envLocked }));
      toast.success(next.active ? 'LLM disabled — credits paused.' : 'LLM enabled — credits will resume.');
    } catch (e) {
      toast.error(e.message || 'Failed to toggle LLM status');
    } finally {
      setBusy(false);
    }
  };

  const baseCls =
    'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border transition-all';
  const stateCls = active
    ? 'bg-rose-500/10 text-rose-500 border-rose-500/30 hover:bg-rose-500/20'
    : 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30 hover:bg-emerald-500/20';
  const cursorCls = isSuper && !(envLocked && active) ? 'cursor-pointer' : 'cursor-default opacity-90';

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={!isSuper || busy || (envLocked && active)}
      title={
        envLocked
          ? 'Locked via LLM_KILLSWITCH env var on server. Remove + restart to enable.'
          : isSuper
            ? (active ? 'Click to enable LLM (will start spending credits)' : 'Click to disable LLM (pause credits)')
            : 'Only Super Admin can toggle.'
      }
      data-testid="llm-status-pill"
      className={`${baseCls} ${stateCls} ${cursorCls} ${className}`}
    >
      {busy ? (
        <Loader2 className="w-3 h-3 animate-spin" />
      ) : active ? (
        <ZapOff className="w-3 h-3" />
      ) : (
        <Zap className="w-3 h-3" />
      )}
      <span data-testid="llm-status-label">
        {active ? 'LLM disabled' : 'LLM live'}
      </span>
      {envLocked && <AlertTriangle className="w-3 h-3 ml-0.5 opacity-70" title="Env-locked" />}
    </button>
  );
}
